/*
external-api-service.js
Adam Berger
July 2023
*/

import axios from "axios";
import { APIError, parseEnvelope } from "./api-envelope";

const MAX_RETRIES = 3
const WAIT_BETWEEN_RETRIES = 1500 // ms

export const callExternalApi = async (url, accessToken) => {
  const config = {
    method: 'GET',
    url: url,
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  };

  return _callExternalApiWithRetries(config, MAX_RETRIES)
}

export const postExternalApi = async (url, body, accessToken, contentType) => {
  const config = {
    method: 'POST',
    url: url,
    data: body,
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': contentType || 'application/json',
    },
  };

  // POSTs are not retried on network errors. A dropped response after the server
  // processed the request would cause duplicate share emails, double recipe creates,
  // etc. Outer call sites (e.g. useEdit.submitImagePart) handle their own retries
  // when the operation is known to be safe.
  return _callExternalApiWithRetries(config, 0);
}

// ====== internal functions ======

function _delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function _unwrap(body, status) {
  try {
    const data = parseEnvelope(body, status);
    return { data, error: null };
  } catch (e) {
    if (e instanceof APIError) {
      return { data: null, error: { code: e.code, message: e.message } };
    }
    return { data: null, error: { code: "UNKNOWN", message: e.message } };
  }
}

const _callExternalApiWithRetries = async (config, numRetriesRemaining) => {
  try {
    const response = await axios(config);
    return _unwrap(response.data, response.status);
  } catch (error) {
    if (error.response) {
      // Server responded with a non-2xx status. The v2 envelope can ride on non-2xx,
      // so route the body through parseEnvelope before falling back.
      console.error("HTTP Error: %s", error.response.status);
      return _unwrap(error.response.data, error.response.status);
    } else if (error.request || error.code === 'ECONNABORTED') {
      // Request was sent but no response was received, or connection aborted. Try again.
      if (numRetriesRemaining > 0) {
        console.log(`"Network Error or No Response. Retry attempt remaining: ${numRetriesRemaining}`);
        console.log("URL: %s; error=%s", config.url, error)
        await _delay(WAIT_BETWEEN_RETRIES)
        return _callExternalApiWithRetries(config, numRetriesRemaining - 1);
      }
      console.log(`Network call failed after ${MAX_RETRIES} attempts.`)
      return { data: null, error: { code: "NETWORK_ERROR", message: error.message } };
    } else {
      // Something else happened while setting up the request - return immediately
      console.error("Request Setup Error:", error.message);
      return { data: null, error: { code: "UNKNOWN", message: error.message } };
    }
  }
}
